import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import fuelApi from '@/services/fuelApi';
import { parseNfeXml, fileToBase64 } from '@/lib/nfeParse';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';

// RefuelDialog — lançamento de abastecimento no CORE (backlog 3.1, proposta
// 2026-07-03 aprovada pelo dono). Mesma semântica do form do lab
// (FuelSettings.jsx), SEM as partes lab-specific (detector client-side,
// saldo_state de cache, calibração): o core só envia o refuel — o saldo é
// computado no SERVIDOR (INV-FUEL-SERVER-AUTHORITATIVE-001) e volta na
// resposta do POST.
//
// Modos (D-38): Manual (fornecedor + nº NF digitados) | NF-e (upload do XML,
// parse no browser via nfeParse — D-39/D-40 — com seleção dos itens diesel).
// Idempotência: client_uuid por tentativa (F1).
//
// Conciliação de detecção (D-11, Plan 07): props opcionais `eventRef` +
// `sensorReadingM3`. Quando presentes (aberto a partir de "Conciliar" na seção
// Detecções pendentes), pré-preenche litros com a subida estimada do sensor
// (editável) e inclui `event_ref`/`sensor_reading_m3` no payload — o backend
// referencia a detecção conciliada e ela some da view de pendências. Sem as
// props (lançamento manual), o comportamento é 100% o de antes.

// Litros já vêm do backend (`*_l`); a tela só formata (ADR-2026-10-25).
const fmtLitros = (l) =>
  l == null || Number.isNaN(l) ? '—' : `${Math.round(l).toLocaleString('pt-BR')} L`;

const DIESEL_RE = /diesel|s-?10|s-?500/i;

const emptyNfe = { nfData: null, nfXmlB64: null, nfPdfB64: null, selected: [], error: null };

export default function RefuelDialog({
  open, onOpenChange, equipmentId, equipmentLabel, onSuccess,
  eventRef = null, sensorReadingM3 = null, sensorReadingL = null,
}) {
  const [litros, setLitros] = useState('');
  const [notes, setNotes] = useState('');
  const [entryMode, setEntryMode] = useState('manual');
  // Manual
  const [supplier, setSupplier] = useState('');
  const [nfNumberManual, setNfNumberManual] = useState('');
  // NF-e
  const [nfe, setNfe] = useState(emptyNfe);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  // Conciliação (D-11): ao abrir com uma detecção, pré-preenche litros com a
  // subida estimada do sensor — permanece EDITÁVEL (o dono pode corrigir).
  // A subida em litros vem pronta do backend (detecção.rise_l).
  useEffect(() => {
    if (open && sensorReadingL != null && Number.isFinite(sensorReadingL)) {
      setLitros(String(Math.round(sensorReadingL)));
    }
  }, [open, sensorReadingL]);

  const resetForm = () => {
    setLitros('');
    setNotes('');
    setEntryMode('manual');
    setSupplier('');
    setNfNumberManual('');
    setNfe(emptyNfe);
  };

  const selectedItems = useMemo(
    () => (nfe.nfData?.items || []).filter((i) => nfe.selected.includes(i.nItem)),
    [nfe],
  );

  async function handleXmlFile(file) {
    if (!file) return;
    try {
      const xml = await file.text();
      const parsed = parseNfeXml(xml);
      const b64 = await fileToBase64(file);
      // Pré-seleciona itens diesel; litros auto-preenche com a soma (editável).
      const diesel = parsed.items.filter((i) => DIESEL_RE.test(i.desc || ''));
      const pre = (diesel.length ? diesel : parsed.items).map((i) => i.nItem);
      setNfe({ nfData: parsed, nfXmlB64: b64, nfPdfB64: null, selected: pre, error: null });
      const qty = parsed.items
        .filter((i) => pre.includes(i.nItem))
        .reduce((s, i) => s + (Number.isFinite(i.quantity) ? i.quantity : 0), 0);
      if (qty > 0) setLitros(String(Math.round(qty)));
    } catch (err) {
      setNfe({ ...emptyNfe, error: err?.message || 'Falha ao ler o XML da NF-e.' });
    }
  }

  async function handlePdfFile(file) {
    if (!file) return;
    try {
      const b64 = await fileToBase64(file);
      setNfe((prev) => ({ ...prev, nfPdfB64: b64 }));
    } catch {
      toast.error('Falha ao ler o PDF/DANFE.');
    }
  }

  const toggleItem = (nItem) =>
    setNfe((prev) => ({
      ...prev,
      selected: prev.selected.includes(nItem)
        ? prev.selected.filter((n) => n !== nItem)
        : [...prev.selected, nItem],
    }));

  async function handleSubmit(e) {
    e.preventDefault();
    const l = parseFloat(litros);
    if (!Number.isFinite(l) || l <= 0) {
      toast.error('Informe os litros abastecidos (> 0).');
      return;
    }
    if (entryMode === 'nfe' && !nfe.nfXmlB64) {
      toast.error('Envie o XML da NF-e ou use o modo Manual.');
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);

    try {
      const ts = new Date().toISOString();
      const clientUuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID() : `refuel-${ts}-${l}`;
      const unitValue = entryMode === 'nfe' ? (selectedItems[0]?.unitValue ?? null) : null;

      const res = await fuelApi.createFuelRecord({
        equipment_id: equipmentId,
        type: 'refuel',
        timestamp: ts,
        // Litros digitados; L → m³ é feito no backend (registry).
        volume_l: l,
        source: 'official',
        client_uuid: clientUuid,
        notes: notes || null,
        entry_mode: entryMode,
        supplier: entryMode === 'nfe' ? (nfe.nfData?.supplier ?? null) : (supplier || null),
        supplier_cnpj: entryMode === 'nfe' ? (nfe.nfData?.cnpj ?? null) : null,
        nf_number: entryMode === 'nfe' ? (nfe.nfData?.nfNumber ?? null) : (nfNumberManual || null),
        nf_key: entryMode === 'nfe' ? (nfe.nfData?.nfKey ?? null) : null,
        product_desc: entryMode === 'nfe' && selectedItems.length
          ? selectedItems.map((i) => i.desc).join('; ') : null,
        quantity_l: entryMode === 'nfe' && selectedItems.length
          ? selectedItems.reduce((s, i) => s + (Number.isFinite(i.quantity) ? i.quantity : 0), 0)
          : null,
        unit_value: unitValue,
        total_value: entryMode === 'nfe' && selectedItems.length
          ? selectedItems.reduce((s, i) => s + (i.totalValue || 0), 0) : null,
        nf_date: entryMode === 'nfe' ? (nfe.nfData?.nfDate ?? null) : null,
        cost: entryMode === 'nfe' && unitValue != null ? l * unitValue : null,
        nf_xml_b64: entryMode === 'nfe' ? nfe.nfXmlB64 : null,
        nf_pdf_b64: entryMode === 'nfe' ? nfe.nfPdfB64 : null,
        // Conciliação (D-11): referencia a detecção pendente conciliada; ausente
        // (null) em lançamento manual — comportamento de antes preservado.
        event_ref: eventRef || null,
        sensor_reading_m3: sensorReadingM3 != null ? sensorReadingM3 : null,
      });

      const saldoAfter = res?.data?.saldo_l_after;
      toast.success(
        saldoAfter != null
          ? `Abastecimento registrado. Saldo: ${fmtLitros(saldoAfter)}`
          : 'Abastecimento registrado.',
      );
      resetForm();
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Falha ao registrar abastecimento.');
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg" data-testid="refuel-dialog">
        <DialogHeader>
          <DialogTitle>Lançar abastecimento</DialogTitle>
          <DialogDescription>
            {equipmentLabel ? `Embarcação: ${equipmentLabel}. ` : ''}
            O saldo é recalculado no servidor a partir deste lançamento.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Tabs value={entryMode} onValueChange={setEntryMode}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="manual">Manual</TabsTrigger>
              <TabsTrigger value="nfe">NF-e</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-2">
            <Label htmlFor="refuel-litros">Litros abastecidos (oficial) *</Label>
            <Input
              id="refuel-litros"
              data-testid="refuel-litros"
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              placeholder="ex.: 7200"
              value={litros}
              onChange={(e) => setLitros(e.target.value)}
              required
            />
          </div>

          {entryMode === 'manual' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="refuel-supplier">Fornecedor</Label>
                <Input
                  id="refuel-supplier"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  placeholder="opcional"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="refuel-nf">Nº da NF</Label>
                <Input
                  id="refuel-nf"
                  value={nfNumberManual}
                  onChange={(e) => setNfNumberManual(e.target.value)}
                  placeholder="opcional"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="refuel-xml" className="flex items-center gap-1">
                    <Upload className="w-3.5 h-3.5" /> XML da NF-e *
                  </Label>
                  <Input
                    id="refuel-xml"
                    type="file"
                    accept=".xml,text/xml"
                    onChange={(e) => handleXmlFile(e.target.files?.[0])}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="refuel-pdf">PDF / DANFE (opcional)</Label>
                  <Input
                    id="refuel-pdf"
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => handlePdfFile(e.target.files?.[0])}
                  />
                </div>
              </div>
              {nfe.error && <p className="text-sm text-red-500">{nfe.error}</p>}
              {nfe.nfData && (
                <div className="rounded-md border p-3 space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    <b className="text-foreground">{nfe.nfData.supplier || '—'}</b>
                    {nfe.nfData.nfNumber ? ` · NF ${nfe.nfData.nfNumber}` : ''}
                  </p>
                  <div className="space-y-1">
                    {nfe.nfData.items.map((i) => (
                      <label key={i.nItem} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={nfe.selected.includes(i.nItem)}
                          onCheckedChange={() => toggleItem(i.nItem)}
                        />
                        <span className="truncate">
                          {i.desc || `Item ${i.nItem}`}
                          <span className="text-muted-foreground">
                            {' '}— {Number.isFinite(i.quantity) ? i.quantity.toLocaleString('pt-BR') : '?'} {i.unit || ''}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="refuel-notes">Observações</Label>
            <Textarea
              id="refuel-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="opcional"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting || !equipmentId} data-testid="refuel-submit">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Registrar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
